/* eslint-disable react-refresh/only-export-components */
/**
 * Document peek header pieces shared by Browse (`document-list-preview-pane`)
 * and the gallery (`/?test=maater-ui#panel-collapse`, `#sidebar-tree`).
 * The button cluster lives in the shell kit `PeekHeader`; the recipe is `DocumentPeekHeader`.
 */
import { Copy, MessageSquarePlus } from "lucide-react"
import { toast } from "sonner"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  displaySegmentsForStoragePath,
  type WorkspaceRouteInfo,
} from "@/lib/app-routes"

export function getDocumentTypeLabel(type: string | null, isQuiz: boolean): string {
  if (isQuiz) return "Quiz"
  if (type === "work_spec") return "Work Spec"
  if (type === "voice_memo") return "Voice Memo"
  if (type === "note") return "Note"
  if (type === "html") return "HTML"
  if (type === "csv") return "CSV"
  if (type === "image") return "Image"
  return "Document"
}

export function DocumentTypeChip({ label }: { label: string }) {
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded border border-border bg-muted text-muted-foreground font-semibold uppercase tracking-wider leading-none flex-shrink-0">
      {label}
    </span>
  )
}

/**
 * Immediate parent folder for a peek header.
 * A file path's last segment is the file; a folder path's last segment is this folder.
 * Either way the parent is the segment before that.
 */
export function peekParentFolderLabel(
  storagePath: string | null | undefined,
  workspaces: WorkspaceRouteInfo[] = [],
): string | null {
  if (!storagePath) return null
  const segments = displaySegmentsForStoragePath(storagePath, workspaces)
  if (segments.length < 2) return null
  const parent = segments[segments.length - 2]?.label?.trim()
  if (!parent) return null
  if (/^ws$/i.test(parent) || /^[a-f0-9]{32}$/i.test(parent)) return null
  return parent
}

/** Header identity: containing folder, then the title. Replaces the type chip. */
export function PeekLocationLine({
  parent,
  title,
  testId,
}: {
  parent?: string | null
  title: string
  testId?: string
}) {
  const label = parent ? `${parent} / ${title}` : title
  return (
    <span
      className="flex min-w-0 flex-1 items-center gap-1 text-sm"
      title={label}
      aria-label={label}
      data-testid={testId}
    >
      {parent ? (
        <span className="max-w-[40%] shrink truncate text-muted-foreground">{parent}</span>
      ) : null}
      {parent ? (
        <span className="shrink-0 text-muted-foreground/50" aria-hidden="true">/</span>
      ) : null}
      <span className="min-w-0 flex-1 truncate font-medium text-foreground">{title}</span>
    </span>
  )
}

export interface DocumentPeekAddToChatDetail {
  type: "document"
  id: string | null
  title: string
  name: string
  documentType: string
  path?: string | null
}

/**
 * Overflow items for a document peek: Copy title, Copy path, Add to chat.
 * Pass `addToChat` to dispatch the shared `add-to-chat` event.
 */
export function DocumentPeekMenuItems({
  title,
  copyPath,
  addToChat,
}: {
  title: string
  copyPath?: string | null
  addToChat?: DocumentPeekAddToChatDetail
}) {
  return (
    <>
      <DropdownMenuItem
        className="gap-2"
        onClick={() => {
          navigator.clipboard.writeText(title)
          toast.success("Title copied", { description: title })
        }}
      >
        <Copy className="h-4 w-4" />
        Copy title
      </DropdownMenuItem>
      {copyPath && (
        <DropdownMenuItem
          className="gap-2"
          onClick={() => {
            navigator.clipboard.writeText(copyPath)
            toast.success("Path copied", { description: copyPath })
          }}
        >
          <Copy className="h-4 w-4" />
          Copy path
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem
        className="gap-2"
        onClick={() => {
          if (addToChat) {
            window.dispatchEvent(new CustomEvent("add-to-chat", { detail: addToChat }))
          }
          toast.success("Added to chat", { description: title })
        }}
      >
        <MessageSquarePlus className="h-4 w-4" />
        Add to chat
      </DropdownMenuItem>
    </>
  )
}
